import { gql } from 'apollo-boost';

export const broadmatchFragment = gql`
    fragment BroadmatchFragment on BroadMatchView {
        enabled
        isLimitDefined
        limit
        goal {
            id
            name
        }
    }
`;

export const TimeTargetFragment = gql`
    fragment TimeTargetFragment on TimeTargetView {
        timeZone {
            id
            name
        }
        monday
        tuesday
        wednesday
        thursday
        friday
        saturday
        sunday
        workingWeekend
        holiday
    }
`;

export const networkFragment = gql`
    fragment NetworkFragment on NetworkView {
        type
        limitPercent
        enableCpcHold
    }
`;

export const demographyMultiplierFragment = gql`
    fragment DemographyMultiplierFragment on DemographyMultiplierView {
        age
        gender
        multiplier
    }
`;

export const retargetingFragment = gql`
    fragment RetargetingFragment on RetargetingMultiplierView {
        retargetingConditionId
        retargetingConditionName
        multiplier
    }
`;
export const restrictFrequencyFragment = gql`
    fragment RestrictFrequencyFragment on RestrictFrequencyView {
        value
        reset
    }
`;

export const flatStrategyFragment = gql`
    fragment FlatStrategyFragment on GdCampaignFlatStrategy {
        strategyType
        ... on GdCampaignStrategyAvgCpaPerFilter {
            filterAvgCpa
            filterAvgBid
            goalId
            goal {
                id
                name
            }
            bid
        }
        ... on GdCampaignStrategyAvgCpcPerFilter {
            bid
            filterAvgBid
        }
        ... on GdCampaignStrategyAvgCpaPerCamp {
            goalId
            goal {
                id
                name
            }
            bid
            avgCpa
            avgBid
        }
        ... on GdCampaignStrategyAvgCpcPerCamp {
            bid
            avgBid
        }
        ... on GdCampaignStrategyAvgCpm {
            avgCpm
        }
        ... on GdCampaignStrategyRoi {
            profitability
            roiCoef
            goalId
            goal {
                id
                name
            }
            bid
            reserveReturn
        }
        ... on GdCampaignStrategyManual {
            separateBidding
        }
        ... on GdStrategyOptimizeClicks {
            avgBid
            clicksLimit
            bid
        }
        ... on GdStrategyOptimizeConversions {
            avgCpa
            goalId
            goal {
                id
                name
            }
            bid
        }
        ... on GdStrategyOptimizeInstalls {
            avgCpi
            goalId
            goal {
                id
                name
            }
            bid
        }
        platform
        isAutoBudget
        budget {
            autoProlongation
            start
            finish
            period
            showMode
            sum
        }
    }
`;

export const logQuery = gql`
    query userActionLogContextQuery(
        $clientLogin: String
        $campaignIds: [Long!]
        $adGroupIds: [Long!]
        $adIds: [Long!]
        $logins: [String!]
        $limit: Int = 200
        $token: String
        $dateFrom: LocalDateTime
        $dateTo: LocalDateTime
        $categories: [CategoryInput!]
        $order: OrderInput
    ) {
        userActionLog(
            clientLogin: $clientLogin
            campaignIds: $campaignIds
            adGroupIds: $adGroupIds
            adIds: $adIds
            logins: $logins
            limit: $limit
            pageToken: $token
            dateFrom: $dateFrom
            dateTo: $dateTo
            categories: $categories
            order: $order
        ) {
            nextPageToken
            logRecords {
                datetime
                user {
                    uid
                    login
                }
                changeSource
                gtid
                event {
                    category
                    ... on CampaignTimeTargetEvent {
                        clientId
                        campaign {
                            id
                            name
                            calcType
                        }
                        oldTimeTarget {
                            ...TimeTargetFragment
                        }
                        newTimeTarget {
                            ...TimeTargetFragment
                        }
                    }
                    ... on CampaignBroadMatchEvent {
                        clientId
                        campaign {
                            id
                            name
                            calcType
                        }
                        oldBroadMatch {
                            ...BroadmatchFragment
                        }
                        newBroadMatch {
                            ...BroadmatchFragment
                        }
                    }
                    ... on CampaignNetworkEvent {
                        clientId
                        campaign {
                            id
                            name
                            calcType
                        }
                        oldNetwork {
                            ...NetworkFragment
                        }
                        newNetwork {
                            ...NetworkFragment
                        }
                    }
                    ... on CampaignRegionsEvent {
                        clientId
                        campaign {
                            id
                            name
                            calcType
                        }
                        oldRegions
                        newRegions
                    }
                    ... on AdGroupsRegionsEvent {
                        clientId
                        campaign {
                            id
                            name
                            calcType
                        }
                        adGroups {
                            id
                            name
                        }
                        oldRegions
                        newRegions
                    }
                    ... on DemographyMultipliersEvent {
                        clientId
                        campaign {
                            id
                            name
                            calcType
                        }
                        adGroup {
                            id
                            name
                        }
                        oldDemographyMultipliers {
                            ...DemographyMultiplierFragment
                        }
                        newDemographyMultipliers {
                            ...DemographyMultiplierFragment
                        }
                    }
                    ... on RetargetingMultipliersEvent {
                        clientId
                        campaign {
                            id
                            name
                            calcType
                        }
                        adGroup {
                            id
                            name
                        }
                        oldRetargetingMultipliers {
                            ...RetargetingFragment
                        }
                        newRetargetingMultipliers {
                            ...RetargetingFragment
                        }
                    }
                    ... on BannersEvent {
                        clientId
                        campaign {
                            id
                            name
                            calcType
                        }
                        adGroup {
                            id
                            name
                        }
                        ads {
                            id
                            title
                        }
                    }
                    ... on CampaignValueChangeEvent {
                        clientId
                        campaign {
                            id
                            name
                            calcType
                        }
                        oldValue
                        newValue
                    }
                    ... on CampaignListChangeEvent {
                        clientId
                        campaign {
                            id
                            name
                            calcType
                        }
                        oldList
                        newList
                    }
                    ... on CampaignStatusChangeEvent {
                        clientId
                        campaign {
                            id
                            name
                            calcType
                        }
                    }
                    ... on CampaignStrategyEvent {
                        clientId
                        campaign {
                            id
                            name
                            calcType
                        }
                        currencyCode
                        oldStrategy {
                            ...FlatStrategyFragment
                        }
                        newStrategy {
                            ...FlatStrategyFragment
                        }
                    }
                    ... on SingleMultiplierEvent {
                        clientId
                        campaign {
                            id
                            name
                            calcType
                        }
                        adGroup {
                            id
                            name
                        }
                        oldMultiplier
                        newMultiplier
                    }
                    ... on CampaignRestrictFrequencyEvent {
                        clientId
                        campaign {
                            id
                            name
                            calcType
                        }
                        oldRestrictFrequency {
                            ...RestrictFrequencyFragment
                        }
                        newRestrictFrequency {
                            ...RestrictFrequencyFragment
                        }
                    }
                }
            }
        }
    }
    ${retargetingFragment}
    ${demographyMultiplierFragment}
    ${networkFragment}
    ${TimeTargetFragment}
    ${broadmatchFragment}
    ${flatStrategyFragment}
    ${restrictFrequencyFragment}
`;
